import { fetcher } from '@commenda/utils';

import {
  IIndirectTaxFilingV2MutationResult,
  IIndirectTaxFilingV2TriggerAggregateSyncParams,
  IIndirectTaxFilingV2TriggerAggregateSyncResponse,
} from './types';

/**
 * POST /api/v1/new-sales-tax/corporations/:corporationId/filing-aggregates/sync
 *
 * Re-runs the per-filing aggregate (filing-numbers) sync for an explicit
 * list of US/CA filings (max 100). Logical backend forwards the
 * `filing_ids[]` JSON body to sales-tax-api-2's
 * `POST /corporations/:id/filing-aggregates/sync`. The endpoint is
 * fire-and-forget and non-idempotent — each call queues fresh background
 * jobs — so the response carries only an acknowledgment message, not a
 * tracking id.
 */
export async function triggerIndirectTaxFilingsV2AggregateSync({
  params,
  token,
}: {
  params: IIndirectTaxFilingV2TriggerAggregateSyncParams;
  token?: string;
}): Promise<
  IIndirectTaxFilingV2MutationResult<IIndirectTaxFilingV2TriggerAggregateSyncResponse>
> {
  try {
    if (!params.filingIds || params.filingIds.length === 0) {
      return { success: false, error: 'filingIds must be a non-empty array' };
    }

    const url = `new-sales-tax/corporations/${params.corporationId}/filing-aggregates/sync`;
    const response = await fetcher(
      url,
      { method: 'POST', body: { filing_ids: params.filingIds } },
      token
    );
    const json = await response.json();
    if (!response.ok) {
      return {
        success: false,
        error: json?.message || 'Failed to trigger aggregate sync',
      };
    }
    return {
      success: true,
      data: { message: json?.message ?? '' },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to trigger aggregate sync',
    };
  }
}
